import React, { useState, useEffect, useMemo } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useParams, useNavigate } from "react-router-dom";
import { Card } from "../../../components/card";
import { Button } from "../../../components/button";
import { ErrorPlaceholder } from "../../../components/common";
import { useToast } from "../../../components/toast-container";
import {
  useGetSurveyDetail,
  useSurveyQuestionEditor,
  useSurveyStatistics,
  useUpdateSurveySubmission,
} from "../hooks";
import { SurveyStatus, type SurveyFormValues } from "../types/survey.types";
import { EDIT_SURVEY_QUESTION_TYPES } from "../data";
import { Eye, Save } from "lucide-react";
import SurveyForm from "../components/form/SurveyForm";
import FormHeader from "../../../components/form/FormHeader";
import QuestionCard from "../components/form/QuestionCard";
import { goBackOrGoHome } from "../../../utils/navigation";
import { createSurveyFormSchema } from "../schemas/survey.schema";
import { getFirstSurveyFormErrorMessage } from "../utils/surveyFormValidation";
import { getSurveyQuestionErrorMessages } from "../utils/surveyQuestionErrorMessages";
import { canEditSurveyField } from "../utils/surveyFormFields";
import { SurveyDetailSkeleton } from "../components/survey-skeletons";

type EditSurveyPageProps = {
  basePath?: string;
};

function EditSurveyPage({ basePath = "/survey" }: EditSurveyPageProps) {
  // const { t } = useTranslation(); // TODO: Add translations for this component
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { refresh: refreshSurveyStats } = useSurveyStatistics();
  const {
    survey: surveyData,
    loading: initialLoading,
    error: surveyError,
    refresh: refreshSurveyDetail,
  } = useGetSurveyDetail(id);
  const { submitUpdate, loading } = useUpdateSurveySubmission();
  const isError = Boolean(surveyError && !surveyData);
  const [previewMode, setPreviewMode] = useState(false);
  const isPublished = Boolean(surveyData && surveyData.status !== SurveyStatus.Unpublished);
  const formSchema = useMemo(
    () =>
      createSurveyFormSchema({
        requireFutureEndTime: isPublished,
        originalEndTime: surveyData?.end_time ? new Date(surveyData.end_time) : undefined,
      }),
    [isPublished, surveyData?.end_time],
  );
  const surveyForm = useForm<SurveyFormValues, undefined, SurveyFormValues>({
    resolver: zodResolver(formSchema),
    mode: "onBlur",
    reValidateMode: "onChange",
    defaultValues: {
      title: "",
      description: "",
      category: "customer_feedback",
      points: 100,
      endTime: undefined,
      organizationPopulationId: null,
      questions: [],
    },
  });
  const formPoints = surveyForm.watch("points");
  const formCategory = surveyForm.watch("category");
  const { questions, lastAddedQuestionId, actions } = useSurveyQuestionEditor(surveyForm);
  const questionErrorMessages = getSurveyQuestionErrorMessages(
    questions,
    surveyForm.formState.errors,
  );
  const canEditQuestions = canEditSurveyField("questions", isPublished);

  useEffect(() => {
    if (!surveyData) {
      return;
    }
    surveyForm.reset({
      title: surveyData?.title || "",
      description: surveyData?.description || "",
      category: surveyData?.category || "customer_feedback",
      points: surveyData?.points || 0,
      endTime: surveyData.end_time ? new Date(surveyData.end_time) : undefined,
      organizationPopulationId: surveyData.organization_population_id,
      questions: Array.isArray(surveyData.questions) ? surveyData.questions : [],
    });
  }, [surveyData, surveyForm]);

  const handleSubmit = surveyForm.handleSubmit(
    async (formValues) => {
      const result = await submitUpdate(id || "", formValues, surveyData?.end_time);
      if (!result.success) return;

      addToast({
        type: "success",
        title: "Success!",
        message: "Survey updated successfully",
      });
      await refreshSurveyStats();
      navigate(`${basePath}/${id}`);
    },
    (errors) => {
      addToast({
        type: "error",
        title: "Error!",
        message:
          getFirstSurveyFormErrorMessage(errors) || "Please check the survey form and try again",
      });
    },
  );
  if (initialLoading) {
    return (
      <div className="container mx-auto p-6 max-w-6xl">
        <SurveyDetailSkeleton />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="container mx-auto p-6 max-w-6xl">
        <Card className="p-6 dark:bg-gray-800 dark:border-gray-600">
          <ErrorPlaceholder
            error={surveyError || "Failed to load survey details"}
            onRetry={() => void refreshSurveyDetail()}
          />
          <div className="flex items-center justify-center gap-2">
            <Button
              variant="outline"
              onClick={() => navigate(basePath)}
            >
              Back to Surveys
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 max-w-6xl">
      <FormHeader
        onBack={() => goBackOrGoHome(navigate)}
        title="Edit Survey"
        subtitle="Modify your survey with a Google Form-like interface"
        className="mb-6"
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => setPreviewMode(!previewMode)}
              className="flex items-center gap-2"
            >
              <Eye className="h-4 w-4" />
              {previewMode ? "Edit" : "Preview"}
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={loading}
              className="flex items-center gap-2 bg-emerald-500 text-white hover:bg-emerald-600"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Updating...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  Update Survey
                </>
              )}
            </Button>
          </div>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          <SurveyForm
            form={surveyForm}
            isPublished={isPublished}
          />

          <QuestionCard
            questions={questions}
            isPreviewMode={previewMode || !canEditQuestions}
            actions={actions}
            validation={{
              message: questionErrorMessages.questionsError,
              questionMessages: questionErrorMessages.questionErrorMessages,
              optionMessages: questionErrorMessages.optionErrorMessages,
              scaleLabelMessages: questionErrorMessages.scaleLabelErrorMessages,
              dateRangeMessages: questionErrorMessages.dateRangeErrorMessages,
              yesNoLabelMessages: questionErrorMessages.yesNoLabelErrorMessages,
            }}
            lastAddedQuestionId={lastAddedQuestionId}
            disabled={!canEditQuestions}
          />
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          <Card className="p-4">
            <h3 className="font-semibold mb-3">Survey Info</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span>Questions:</span>
                <span className="font-medium">{questions.length}</span>
              </div>
              <div className="flex justify-between">
                <span>Required:</span>
                <span className="font-medium">{questions.filter((q) => q.is_required).length}</span>
              </div>
              <div className="flex justify-between">
                <span>Points:</span>
                <span className="font-medium">{formPoints}</span>
              </div>
              <div className="flex justify-between">
                <span>Category:</span>
                <span className="font-medium">{formCategory.replace("_", " ")}</span>
              </div>
            </div>
          </Card>

          <Card className="p-4">
            <h3 className="font-semibold mb-3">Question Types</h3>
            <div className="space-y-2 text-sm">
              {EDIT_SURVEY_QUESTION_TYPES.map((type) => (
                <div
                  key={type.value}
                  className="flex items-center gap-2"
                >
                  <span>{type.icon}</span>
                  <span>{type.label}</span>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}

export default EditSurveyPage;
